import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { FamilyGroupService, type CreateFamilyGroupRequest, type UpdateFamilyGroupRequest } from '@/api/familyGroups';
import { FamilyGroupsService } from '@/api';
import type { billing_FamilyGroupMemberRequest } from '@/api';
import type { FamilyGroupPickerListResponse } from '@/types/billing';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { extractBackendError, translateBackendError } from '@/lib/translateBackendError';

// Query keys for family groups
export const familyGroupKeys = {
  all: ['familyGroups'] as const,
  lists: () => [...familyGroupKeys.all, 'list'] as const,
  byChild: (childId: string) => [...familyGroupKeys.lists(), 'child', childId] as const,
  detail: (id: string) => [...familyGroupKeys.all, 'detail', id] as const,
  // Under lists() so every family-group mutation that invalidates lists()
  // or all also refreshes the picker rows (balances, members, children).
  picker: (orgId: string | null | undefined, params: FamilyGroupPickerParams) =>
    [...familyGroupKeys.lists(), 'picker', orgId ?? null, params] as const,
};

/** Query parameters of GET /api/v1/family-groups (the family-account picker). */
export interface FamilyGroupPickerParams {
  /** Family name or a child's internal id / first / last name. */
  search?: string;
  /** Child whose parent contacts drive `sharesParent` (and the sort order). */
  childId?: string;
  excludeFamilyGroupId?: string;
  limit?: number;
  offset?: number;
}

export const FAMILY_GROUP_PICKER_PAGE_SIZE = 20;

/**
 * One page of the organisation's family groups for the family-account picker:
 * members, children, debtor balances and lifecycle per group. Rows that share
 * a parent contact with `childId` come first (`sharesParent`).
 */
export function useFamilyGroupPicker(params: FamilyGroupPickerParams, enabled = true) {
  const { isSignedIn, orgId } = useAuth();
  const normalized: FamilyGroupPickerParams = {
    search: params.search?.trim() || undefined,
    childId: params.childId || undefined,
    excludeFamilyGroupId: params.excludeFamilyGroupId || undefined,
    limit: params.limit ?? FAMILY_GROUP_PICKER_PAGE_SIZE,
    offset: params.offset ?? 0,
  };

  return useQuery<FamilyGroupPickerListResponse>({
    queryKey: familyGroupKeys.picker(orgId, normalized),
    queryFn: () =>
      FamilyGroupsService.getApiV1FamilyGroups(
        normalized.search,
        normalized.childId,
        normalized.excludeFamilyGroupId,
        normalized.limit,
        normalized.offset,
      ),
    enabled: enabled && !!isSignedIn,
    // Balances move with every payment; keep the window short like the debtor list.
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/** Outcome of `useAddFamilyGroupMembersBestEffort`. */
export interface AddFamilyGroupMembersResult {
  /** Contacts whose add request failed. */
  failed: string[];
  /**
   * Contacts not added because the group bills SPLIT: a member without a
   * payment percentage breaks the group's 100 % split, so the split must be
   * edited by hand.
   */
  skippedForSplit: string[];
}

/**
 * Adds contacts to an existing UNIFIED family group, one request per contact.
 * Best-effort: resolves with the contacts that failed or were skipped instead
 * of throwing, so a caller can report them without undoing the action that
 * came before. A SPLIT group gets no automatic members (see `skippedForSplit`).
 */
export function useAddFamilyGroupMembersBestEffort() {
  const queryClient = useQueryClient();

  return useCallback(async (
    groupId: string,
    contactIds: string[],
    billingType: 'UNIFIED' | 'SPLIT',
  ): Promise<AddFamilyGroupMembersResult> => {
    if (contactIds.length === 0) return { failed: [], skippedForSplit: [] };
    if (billingType === 'SPLIT') return { failed: [], skippedForSplit: contactIds };
    const results = await Promise.allSettled(
      contactIds.map((contactId) =>
        FamilyGroupsService.postApiV1FamilyGroupsMembers(groupId, {
          contact_id: contactId,
          member_role: 'PARENT' as billing_FamilyGroupMemberRequest.member_role,
          can_view_invoices: true,
          can_make_payments: true,
          receives_separate_invoice: false,
        }),
      ),
    );
    await queryClient.invalidateQueries({ queryKey: familyGroupKeys.all });
    return {
      failed: contactIds.filter((_, i) => results[i].status === 'rejected'),
      skippedForSplit: [],
    };
  }, [queryClient]);
}

// Hook to fetch family groups for a specific child
export function useFamilyGroupsByChild(childId: string, enabled = true) {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: familyGroupKeys.byChild(childId),
    queryFn: () => FamilyGroupService.getFamilyGroupsByChild(childId),
    enabled: enabled && isSignedIn && !!childId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to fetch a single family group
export function useFamilyGroup(id: string, enabled = true) {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: familyGroupKeys.detail(id),
    queryFn: () => FamilyGroupService.getFamilyGroup(id),
    enabled: enabled && isSignedIn && !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to create a family group
export function useCreateFamilyGroup(options?: {
  /** No success toast: the caller creates the group as one step of a larger action and reports once. */
  silent?: boolean;
}) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const silent = options?.silent ?? false;

  return useMutation({
    mutationFn: (data: CreateFamilyGroupRequest) => FamilyGroupService.createFamilyGroup(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: familyGroupKeys.all });
      queryClient.invalidateQueries({ queryKey: familyGroupKeys.byChild(data.child_id) });
      if (!silent) {
        toast.success(i18n._(msg`Family group created successfully`));
      }
    },
    onError: (error: unknown) => {
      console.error('Failed to create family group:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to create family group`));
    },
  });
}

// Hook to update a family group
export function useUpdateFamilyGroup(id: string) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (data: UpdateFamilyGroupRequest) => FamilyGroupService.updateFamilyGroup(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyGroupKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: familyGroupKeys.lists() });
      toast.success(i18n._(msg`Family group updated successfully`));
    },
    onError: (error: unknown) => {
      console.error('Failed to update family group:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to update family group`));
    },
  });
}

// Hook to delete a family group
export function useDeleteFamilyGroup() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (id: string) => FamilyGroupService.deleteFamilyGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyGroupKeys.all });
      toast.success(i18n._(msg`Family group deleted successfully`));
    },
    onError: (error: unknown) => {
      console.error('Failed to delete family group:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to delete family group`));
    },
  });
}

// Hook to add a member to a family group
export function useAddFamilyGroupMember(groupId: string) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (member: Parameters<typeof FamilyGroupService.addMember>[1]) =>
      FamilyGroupService.addMember(groupId, member),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyGroupKeys.detail(groupId) });
      toast.success(i18n._(msg`Member added to family group`));
    },
    onError: (error: unknown) => {
      console.error('Failed to add member:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to add member to family group`));
    },
  });
}

// Hook to remove a member from a family group
export function useRemoveFamilyGroupMember(groupId: string) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (memberId: string) => FamilyGroupService.removeMember(groupId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyGroupKeys.detail(groupId) });
      toast.success(i18n._(msg`Member removed from family group`));
    },
    onError: (error: unknown) => {
      console.error('Failed to remove member:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to remove member from family group`));
    },
  });
}